import { request } from './request';
import { encrypt } from '@/utils/crypto';

interface UpdateUserParams {
  name?: string;
  birthday?: string;
  nationality?: string;
  roleId: number
}
// 登录请求参数
export interface LoginParams {
  username: string;
  password: string;
  clientType: string;
  scope: string;
  loginType: string;
}

// 登录响应数据
export interface LoginResponse {
  accessToken: string;
  lincId: string;
}

// 用户信息响应
export interface UserInfoResponse {
  id: string;
  username: string;
  email: string;
  name?: string;
  avatar?: string;
  enterpriseId?: string;
  permissions?: string[];
  roleInfo?: {
    name?: string;
    id: number
  };
  [key: string]: any; // 允许其他字段
}

// 登录接口
export function loginApi(params: { username: string; password: string }): Promise<LoginResponse> {
  // 加密密码
  const encryptedPassword = encrypt(params.password);
  
  return request({
    url: '/id-auth/api/v1/login',
    method: 'POST',
    data: {
      username: params.username,
      password: encryptedPassword,
      clientType: 'linc-retention-support',
      scope: 'user.r',
      loginType: 'password_login'
    }
  });
}

// 获取用户信息接口
export function getUserInfoApi(): Promise<UserInfoResponse> {
  return request({
    url: '/id-auth/api/v1/rs-account/info',
    method: 'GET'
  });
}
// 获取用户信息接口
export function updateUserInfoApi(id: string, params: UpdateUserParams): Promise<UserInfoResponse> {
  return request({
    url: `/rs-enterprise/api/v1/account/${id}`,
    method: 'PUT',
    data: params
  });
}

/** 账号在某个企业下、某个产品中的角色 */
export interface EnterpriseProductRole {
  /** 产品ID：1-定着支援(TALENT)，2-CAREER/ATS(企业端)，3-猎头端 */
  productId: number;
  /** 角色ID */
  roleId: number;
  /** 角色名，如 ROLE_BUSINESS_ADMIN */
  roleName: string;
}

/** 账号所属的企业。同一账号在不同企业下的产品与角色可以不同 */
export interface MyEnterpriseItem {
  enterpriseId: number;
  enterpriseName: string;
  /** 是否为当前所在企业 */
  current: boolean;
  products: EnterpriseProductRole[];
}

export interface SwitchEnterpriseParams {
  /** 目标企业ID */
  enterpriseId: number;
}

/**
 * 我的企业列表
 * 注意：响应拦截器已拆掉 { code, message, data } 信封，这里直接拿到 data
 */
export function myEnterprises(): Promise<MyEnterpriseItem[]> {
  return request({
    url: '/id-auth/api/v1/my-enterprises',
    method: 'GET'
  });
}

/**
 * 切换企业：持久化服务端的「当前企业」
 * 实际的数据作用域由每个请求的 x-enterprise-id 决定，本接口不是切换生效的必要条件，
 * 但需等它成功后再刷新，避免服务端 current 与前端不一致
 * @returns 切换后的企业列表（current 已更新）
 */
export function switchEnterprise(params: SwitchEnterpriseParams): Promise<MyEnterpriseItem[]> {
  return request({
    url: '/id-auth/api/v1/switch-enterprise',
    method: 'POST',
    data: params
  });
}

/** 账号在「当前企业」下对某个产品的访问权限 */
export interface ProductAccessItem {
  /** 产品ID：1-定着支援(TALENT)，2-CAREER/ATS(企业端)，3-猎头端 */
  productId: number;
  /** 角色ID */
  roleId: number;
  /** 角色名，如 ROLE_BUSINESS_ADMIN */
  roleName: string;
  /** 是否为当前所在产品 */
  current: boolean;
}

export interface SwitchProductParams {
  /** 目标产品ID */
  productId: number;
}

/**
 * 切换产品：校验账号在「当前企业」下对目标产品的访问权限，并记录最近使用产品
 * 当前产品由每个请求的 x-product-id 决定，无需重签 token，因此切换成功后直接跳转目标产品即可
 * 无权限时接口报错，调用方据此拦截跳转
 * @returns 当前企业下可访问的产品列表（current 已更新为目标产品）
 */
export function switchProduct(params: SwitchProductParams): Promise<ProductAccessItem[]> {
  return request({
    url: '/id-auth/api/v1/switch-product',
    method: 'POST',
    data: params
  });
}

// 登出接口
export function logoutApi(): Promise<void> {
  return request({
    url: '/id-auth/api/v1/logout',
    method: 'POST'
  });
}

// 忘记密码接口
export function forgotPasswordApi(email: string): Promise<void> {
  return request({
    url: '/id-auth/api/v1/password-reset/request',
    method: 'POST',
    data: {
      email
    }
  });
}

// 确认密码重置接口
export function confirmPasswordResetApi(params: { token: string; newPassword: string }): Promise<void> {
  // 加密密码
  const encryptedPassword = encrypt(params.newPassword);
  
  return request({
    url: '/id-auth/api/v1/password-reset/confirm',
    method: 'POST',
    data: {
      token: params.token,
      newPassword: encryptedPassword
    }
  });
}

